import React, { useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { MainContentHeaders } from './utils';

const instructionsContainerStyles = xcss({
	border: `1px solid ${token('color.border.input')}`,
	borderRadius: 'border.radius.100',
	overflowY: 'auto',
	whiteSpace: 'pre-line',
	position: 'relative',
	maxHeight: '100%',
	padding: 'space.100',
	flex: '1',
});

const copyContainerStyles = xcss({
	position: 'absolute',
	top: '10px',
	right: '10px',
});

type AgentInstructionsProps = {
	promptTemplate?: string | null;
	onCopy: (promptTemplate: string) => void;
};

const hiddenStyles = xcss({
	opacity: 0,
});

export const AgentInstructions = ({ promptTemplate, onCopy }: AgentInstructionsProps) => {
	const { formatMessage } = useIntl();
	const [instructionsHovered, setInstructionsHovered] = useState(false);
	const [mouseEnterHappened, setMouseEnterHappened] = useState(false);

	return (
		<Stack space="space.200" grow="fill">
			<MainContentHeaders>{formatMessage(messages.instructionsLabel)}</MainContentHeaders>

			<Box
				xcss={instructionsContainerStyles}
				onMouseEnter={() => {
					setMouseEnterHappened(true);
					setInstructionsHovered(true);
				}}
				onMouseLeave={() => {
					setMouseEnterHappened(false);
					setInstructionsHovered(false);
				}}
				onFocus={() => setInstructionsHovered(true)}
				onBlur={() => {
					if (!mouseEnterHappened) {
						setInstructionsHovered(false);
					}
				}}
			>
				{promptTemplate ? (
					<>
						<Text>{promptTemplate}</Text>
						<Box xcss={[copyContainerStyles, !instructionsHovered && hiddenStyles]}>
							<Tooltip content={formatMessage(messages.copyInstructionsTooltip)} position="top">
								<IconButton
									type="button"
									onClick={() => onCopy(promptTemplate)}
									icon={CopyIcon}
									appearance="subtle"
									spacing="compact"
									label={formatMessage(messages.copyInstructionsLabel)}
								/>
							</Tooltip>
						</Box>
					</>
				) : (
					<Text as="em" color="color.text.subtle">
						{formatMessage(messages.noInstructionPlaceholder)}
					</Text>
				)}
			</Box>
		</Stack>
	);
};

const messages = defineMessages({
	instructionsLabel: {
		id: 'ptc-directory.agent-profile.instructions-label',
		defaultMessage: 'Instructions',
		description: 'Label for instructions',
	},
	copyInstructionsLabel: {
		id: 'ptc-directory.agent-profile.copy-instructions-label',
		defaultMessage: 'Copy',
		description: 'Label for copy instructions button',
	},
	copyInstructionsTooltip: {
		id: 'ptc-directory.agent-profile.copy-instructions-tooltip',
		defaultMessage: 'Copy response',
		description: 'Label for copy instructions tooltip',
	},
	noInstructionPlaceholder: {
		id: 'ptc-directory.agent-profile.no-instruction-placeholder',
		defaultMessage: 'This Agent has no custom instructions',
		description: 'Placeholder for no instructionsfdkggkh',
	},
});
